import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import { useSnackbar } from "notistack";
import { get_staff_courses_api, update_staff_courses_api } from "../../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, font, accent, success, warning, easing,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * Who may teach what, and who may price it.
 *
 * The server has enforced this since the entitlement layer shipped; there was
 * no way to set it, so every instructor sat on the default and the default is
 * "everything". A rule with no UI is not a rule.
 *
 * TWO SEPARATE FACTS, DELIBERATELY NOT MERGED
 * "Approved for nothing in particular" and "approved for exactly these" look
 * identical in an empty checkbox list, and they mean opposite things: the first
 * inherits everything the school sells. So the row says which state it is in,
 * in words, and clearing every box returns to inheritance rather than to
 * "cannot teach anything".
 */
export default function InstructorCourses() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    const res = await get_staff_courses_api();
    if (res.code === 200) setData(res);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (staff, patch) => {
    setSavingId(staff.id);
    const res = await update_staff_courses_api(staff.id, {
      courses: patch.courses !== undefined ? patch.courses : staff.approvedCourses,
      canSetOwnRates: patch.canSetOwnRates !== undefined
        ? patch.canSetOwnRates
        // The resolved value, not the stored one. Sending null here would
        // silently reset an instructor to "follow the school" every time
        // someone changed their COURSES, which is a different control.
        : staff.canSetOwnRates,
    });
    if (res.code === 200) { await load(); }
    else enqueueSnackbar(res.message || "Couldn't save", { variant: "error" });
    setSavingId(null);
  };

  const toggleCourse = (staff, license) => {
    const has = staff.approvedCourses.includes(license);
    const next = has ? staff.approvedCourses.filter((c) => c !== license) : [...staff.approvedCourses, license];
    save(staff, { courses: next });
  };

  if (loading) return <CircularProgress size={20} sx={{ color: accent }} />;
  if (!data) return <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary }}>Couldn't load staff courses.</Typography>;

  const teachers = (data.staff || []).filter((s) => s.bookable || s.approvedCourses.length);

  return (
    <Box>
      <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 2, lineHeight: 1.55, maxWidth: 620 }}>
        Which of your courses each instructor may be booked for, and whether they set their own price. An instructor with
        nothing ticked teaches everything your school offers — that is the default, not a gap.
      </Typography>

      {!teachers.length && (
        <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, lineHeight: 1.5 }}>
          No bookable instructors yet. Someone appears here once they turn on bookings from My Teaching.
        </Typography>
      )}

      {teachers.map((s) => (
        <Box key={s.id} sx={{ border: `1px solid ${border}`, borderRadius: "10px", p: 1.5, mb: 1, backgroundColor: "#fff", opacity: savingId === s.id ? 0.6 : 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>{s.name}</Typography>
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>{s.email}</Typography>
            {!s.bookable && (
              <Chip label="NOT BOOKABLE" size="small" sx={{
                height: 17, fontSize: "9.5px", fontWeight: 700, fontFamily: font, borderRadius: "4px",
                backgroundColor: `${warning}14`, color: warning,
              }} />
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
            {(data.catalogue || []).map((c) => {
              const on = s.approvedCourses.includes(c.license);
              return (
                <Box
                  key={c.license}
                  onClick={() => savingId !== s.id && toggleCourse(s, c.license)}
                  sx={{
                    px: 1.25, py: 0.4, borderRadius: "999px", cursor: "pointer", fontFamily: font,
                    fontSize: "12px", fontWeight: 600, transition: `all 0.15s ${easing}`,
                    color: on ? "#fff" : textSecondary,
                    backgroundColor: on ? accent : "transparent",
                    border: `1px solid ${on ? accent : border}`,
                  }}
                >
                  {on ? "✓ " : ""}{c.label}
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: s.inheritsAllCourses ? textSecondary : success, mb: 1 }}>
            {s.inheritsAllCourses
              ? `Nothing ticked — teaches everything the school offers (${s.effectiveCourses.length} course${s.effectiveCourses.length === 1 ? "" : "s"}).`
              : `Bookable for ${s.effectiveCourses.length} of ${(data.catalogue || []).length}.`}
          </Typography>

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", pt: 1, borderTop: `1px solid ${border}` }}>
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mr: 0.5 }}>
              Pricing{!s.pricingIsExplicit ? ` (school default: ${data.schoolRateMode === "instructor_set" ? "own rate" : "school rate"})` : ""}:
            </Typography>
            {/* Two options, because there are two answers.
                There used to be three — "Follow the school", "Sets their own"
                and "School rate only" — and the first and third were the same
                thing whenever the school default was the school rate, so the
                control asked a question with a duplicate answer in it.
                Inheritance still exists in the data (null means follow the
                school) and is what an untouched instructor has; picking either
                option here just makes it explicit for that person. */}
            {[
              { v: false, label: "School rate" },
              { v: true, label: "Sets their own" },
            ].map((o) => {
              // The RESOLVED value, so the highlighted option is what actually
              // happens today — not "nothing chosen yet".
              const on = s.canSetOwnRates === o.v;
              return (
                <Box
                  key={String(o.v)}
                  onClick={() => savingId !== s.id && save(s, { canSetOwnRates: o.v })}
                  sx={{
                    px: 1, py: 0.3, borderRadius: "6px", cursor: "pointer", fontFamily: font,
                    fontSize: "11.5px", fontWeight: 600, transition: `all 0.12s ${easing}`,
                    color: on ? textPrimary : textSecondary,
                    backgroundColor: on ? "#F5F2EF" : "transparent",
                    border: `1px solid ${on ? textPrimary : border}`,
                  }}
                >
                  {o.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
